import React, { useMemo } from 'react'
import { Flex, Text, TextVariant, BurnIcon } from '@mprolab/uikit'
import { Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import useScreenSize from 'hooks/useScreenSize'
import Image from 'components/CompatImage'
import isNil from 'lodash/isNil'
import { tokenLogoSrc } from 'utils/tokenLogoUri'
import StakeProcess from './StakeProcess'
import { AggregatedClassicStakeConfig } from '../classicStake.logic'
import StakeCounter from './StakeCounter'

// Styled components
const TickerWrapper = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: max-content;
  border-radius: 24px;
  padding: 8px 13px 8px 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

const Column = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`

// Interfaces
interface StakeTableContentProps {
  stakeConfig: AggregatedClassicStakeConfig
  loadingData: boolean
}

interface AmountContainerBaseProps {
  value: number
  loadingData: boolean
  icon?: React.ReactElement
  title?: string
  isMobile?: boolean
}

// Constants
const IMAGE_SIZE = 16
const MOBILE_COLUMN_WIDTH = '150px'

// Custom hooks
const useStakeTokenTicker = (stakeConfig: AggregatedClassicStakeConfig) => {
  return useMemo(() => {
    const stake = stakeConfig?.stake
    const isPair = !isNil(stake?.token0 && stake?.token1)
    const tokenAImage = isPair ? stake?.token0?.logoURI : stake?.stakeToken?.logoURI
    const tokenBImage = stake?.token1?.logoURI

    return (
      <TickerWrapper width={MOBILE_COLUMN_WIDTH}>
        <Flex>
          <Image src={tokenLogoSrc(tokenAImage)} width={IMAGE_SIZE} height={IMAGE_SIZE} />
          {tokenBImage && <Image src={tokenLogoSrc(tokenBImage)} width={IMAGE_SIZE} height={IMAGE_SIZE} />}
        </Flex>
        <Text variant={isPair ? TextVariant.TEXT_XS_BOLD : TextVariant.BODY_SMALL_BOLD}>
          {isPair ? `${stake.token0.token}-${stake.token1.token}` : stake.stakeToken?.token}
        </Text>
      </TickerWrapper>
    )
  }, [stakeConfig])
}

// Components
const RewardTokenTicker: React.FC<{ stake: AggregatedClassicStakeConfig['stake'] }> = React.memo(({ stake }) => (
  <TickerWrapper width={MOBILE_COLUMN_WIDTH}>
    <Flex>
      <Image src={tokenLogoSrc(stake?.rewardToken?.logoURI)} width={IMAGE_SIZE} height={IMAGE_SIZE} />
    </Flex>
    <Text variant={TextVariant.BODY_SMALL_BOLD}>{stake.rewardToken?.token}</Text>
  </TickerWrapper>
))
RewardTokenTicker.displayName = 'RewardTokenTicker'

const AmountContainerBase: React.FC<AmountContainerBaseProps> = React.memo(
  ({ value, loadingData, icon, title, isMobile }) => {
    const { colors } = useTheme()

    const content = loadingData ? (
      <UseAnimations animation={loading} strokeColor={colors.gray6} />
    ) : (
      <Flex>
        <Text variant={TextVariant.BODY_SMALL} verticalAlign="center">
          {!isNil(value) && value > 0 ? `${value}%` : '-'}
        </Text>
        {icon && React.cloneElement(icon)}
      </Flex>
    )

    if (isMobile) {
      return (
        <Column width={MOBILE_COLUMN_WIDTH}>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {title}
          </Text>
          {content}
        </Column>
      )
    }

    return (
      <Grid item xs={1}>
        {content}
      </Grid>
    )
  }
)
AmountContainerBase.displayName = 'AmountContainerBase'

const MobileView: React.FC<StakeTableContentProps> = React.memo(({ stakeConfig, loadingData }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const stakeTokenTicker = useStakeTokenTicker(stakeConfig)
  const stake = stakeConfig?.stake

  return (
    <Flex flexDirection="column" gap="16px" maxWidth="250px">
      <Flex fullWidth justifyContent="space-between">
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('stakingPanel.stake')}
        </Text>
        <StakeProcess stakeConfig={stakeConfig} />
      </Flex>
      <Flex>
        <Column width={MOBILE_COLUMN_WIDTH}>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('stakingPanel.earn')}
          </Text>
          {stakeTokenTicker}
        </Column>
        <Column width={MOBILE_COLUMN_WIDTH}>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('stakingPanel.token')}
          </Text>
          <RewardTokenTicker stake={stake} />
        </Column>
      </Flex>
      <Flex alignItems="space-around">
        <AmountContainerBase title={t('stakingPanel.apr')} value={stake?.apr} loadingData={loadingData} isMobile />
        <AmountContainerBase title={t('stakingPanel.apy')} value={stake?.apy} loadingData={loadingData} isMobile />
        <AmountContainerBase title={t('stakingPanel.fee')} value={stake?.fee} loadingData={loadingData} isMobile />
        <AmountContainerBase
          title={t('stakingPanel.burn')}
          value={stake?.burnFee}
          loadingData={loadingData}
          isMobile
          icon={<BurnIcon fill="#F6465D" />}
        />
      </Flex>
    </Flex>
  )
})
MobileView.displayName = 'MobileView'

const DesktopView: React.FC<StakeTableContentProps> = React.memo(({ stakeConfig, loadingData }) => {
  const stakeTokenTicker = useStakeTokenTicker(stakeConfig)
  const stake = stakeConfig?.stake

  return (
    <Grid container display="flex" alignItems="center" padding="8px">
      <Grid item xs={1}>
        <StakeProcess stakeConfig={stakeConfig} />
      </Grid>
      <Grid item xs={1.4}>
        {stakeTokenTicker}
      </Grid>
      <Grid item xs={2}>
        <RewardTokenTicker stake={stake} />
      </Grid>
      <Grid item xs={2.8}>
        <Flex maxWidth="208px">
          <StakeCounter stake={stake} />
        </Flex>
      </Grid>
      <AmountContainerBase value={stake?.apr} loadingData={loadingData} />
      <AmountContainerBase value={stake?.apy} loadingData={loadingData} />
      <AmountContainerBase value={stake?.fee} loadingData={loadingData} />
      <AmountContainerBase value={stake?.burnFee} loadingData={loadingData} icon={<BurnIcon fill="#F6465D" />} />
    </Grid>
  )
})
DesktopView.displayName = 'DesktopView'

const StakeTableContent: React.FC<StakeTableContentProps> = ({ stakeConfig, loadingData }) => {
  const { isMobile } = useScreenSize()

  return isMobile ? (
    <MobileView stakeConfig={stakeConfig} loadingData={loadingData} />
  ) : (
    <DesktopView stakeConfig={stakeConfig} loadingData={loadingData} />
  )
}

export default React.memo(StakeTableContent)
